import { Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, take } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Blocks until session restore has settled.
 *
 * Angular runs a route's `canActivate` guards **concurrently**, so a guard
 * listed after `authGuard` cannot assume `authGuard` has already waited. Any
 * guard that touches the API or reads `auth.user()` has to wait for itself.
 *
 * Skipping this produced a 401 on every cold load of a `/:companySlug/...`
 * route: `companyAccessGuard` fired `GET /api/companies` while the access token
 * — memory-only since the move to cookie auth — was still null, so the request
 * went out with no `Authorization` header. The interceptor recovered it, so the
 * app worked and only the console showed it.
 *
 * Uses a signal→observable bridge rather than a timer so a cancelled navigation
 * unsubscribes cleanly instead of leaving work running against a stale context.
 * `injector` is passed explicitly so callers may await before calling this.
 */
export async function awaitSessionRestore(auth: AuthService, injector: Injector): Promise<void> {
	if (!auth.loading()) return;
	await firstValueFrom(
		toObservable(auth.loading, { injector }).pipe(
			filter((loading) => !loading),
			take(1),
		),
	);
}
